import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UI_THEME } from '../ui/theme';
import SolicitacaoForm from './SolicitacaoForm';
import PedidoEnviado from './PedidoEnviado';
import StatusPedido from './StatusPedido';
import {
  rotuloAlvo,
  rotuloCategoria,
  solicitacaoService,
  ultimaMensagemDaEquipe,
  type Solicitacao,
} from '../services/solicitacaoService';

const formatarData = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

/** O que o parlamentar vê: fazer um pedido e acompanhar os que já fez, em cartões. */
const MinhasSolicitacoes: React.FC = () => {
  const navigate = useNavigate();
  const isSaas = UI_THEME === 'saas';
  const [novo, setNovo] = useState(false);
  const [enviado, setEnviado] = useState<{ solicitacao: Solicitacao; falhas: string[] } | null>(null);
  const minhas = useQuery({ queryKey: ['solicitacoes', 'minhas'], queryFn: solicitacaoService.minhas });
  const surface = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const accent = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const primary = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';

  return (
    <div className="space-y-6">
      <section className={`rounded-3xl border p-5 ${surface}`}>
        {enviado ? (
          <PedidoEnviado
            solicitacao={enviado.solicitacao}
            falhasDeAnexo={enviado.falhas}
            rotuloFechar="Fazer outro pedido"
            onVerPedido={() => navigate(`/dashboard/solicitacoes/${enviado.solicitacao.id}`)}
            onFechar={() => {
              setEnviado(null);
              setNovo(true);
            }}
          />
        ) : novo ? (
          <>
            <h2 className="mb-5 text-xl font-bold text-slate-900 dark:text-slate-100">Fazer um pedido</h2>
            <SolicitacaoForm
              onCancelar={() => setNovo(false)}
              onCriada={(criada, falhas) => {
                setNovo(false);
                setEnviado({ solicitacao: criada, falhas });
              }}
            />
          </>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-base leading-7 text-slate-700 dark:text-slate-200">
              Precisa mudar alguma coisa ou tirar uma dúvida? Faça um pedido e a equipe responde por aqui e por e-mail.
            </p>
            <button
              type="button"
              onClick={() => setNovo(true)}
              className={`min-h-[52px] shrink-0 rounded-xl px-6 py-3 text-base font-semibold transition ${primary}`}
            >
              Fazer um pedido
            </button>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-xl font-bold text-slate-900 dark:text-slate-100">Meus pedidos</h2>
        {minhas.isLoading && <p className="text-base text-slate-500 dark:text-slate-400">Carregando...</p>}
        {minhas.isError && (
          <p className="text-base text-red-600 dark:text-red-400">Não foi possível carregar seus pedidos. Tente de novo em instantes.</p>
        )}
        {minhas.data && minhas.data.length === 0 && (
          <p className={`rounded-2xl border p-5 text-base text-slate-600 dark:text-slate-300 ${surface}`}>
            Você ainda não fez nenhum pedido.
          </p>
        )}
        {minhas.data && minhas.data.length > 0 && (
          <div className={`overflow-hidden rounded-2xl border ${surface}`}>
            <table className="w-full text-left text-base">
              <thead className="bg-slate-50 text-sm text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
                <tr>
                  <th className="px-4 py-2 font-semibold">Pedido</th>
                  <th className="px-4 py-2 font-semibold">Assunto</th>
                  <th className="hidden px-4 py-2 font-semibold md:table-cell">Enviado em</th>
                  <th className="px-4 py-2 font-semibold">Situação</th>
                  <th className="hidden px-4 py-2 font-semibold lg:table-cell">Resposta da equipe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {minhas.data.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/dashboard/solicitacoes/${s.id}`)}
                    className="cursor-pointer text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800/60"
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <Link to={`/dashboard/solicitacoes/${s.id}`} className={`font-bold hover:underline ${accent}`}>
                        nº {s.numero ?? s.protocolo}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {rotuloCategoria(s.categoria, s.categoriaOutro)}
                      <span className="block text-sm text-slate-500 dark:text-slate-400">{rotuloAlvo(s)}</span>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-3 md:table-cell">{formatarData(s.criadaEm)}</td>
                    <td className="px-4 py-3"><StatusPedido status={s.status} /></td>
                    <td className="hidden max-w-xs truncate px-4 py-3 lg:table-cell" title={ultimaMensagemDaEquipe(s) ?? ''}>
                      {ultimaMensagemDaEquipe(s) ?? <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default MinhasSolicitacoes;
